/**
 * WorkspaceSession — Simplified SDK wrapper (§80 §5.2)
 *
 * Provides a streamlined API for SDK consumers (customer apps, scripts).
 * Delegates to the full Workspace interface internally.
 * Guards: WRITE_FORBIDDEN (read-only), WORKSPACE_CLOSED (after close()).
 * 
 * Mapped 1:1 to .NET SDK's IWorkspaceSession (§61 §6.3).
 */

import type {
    Workspace,
    WorkspaceEventType,
    WorkspaceEventPayload,
    FsChangeEvent,
    FsExternalChangeEvent,
    FileStat,
    SyncState,
    CommitSummary,
    BlameLine,
    ImportFileEntry,
    ImportFilesOptions as EngineImportOpts,
    ImportFilesResult as EngineImportResult,
    ReadTextOptions,
    WorkspaceSyncPolicy,
    WorkspaceResourceLease,
    OpenSqliteOptions,
    SqliteBackgroundSyncMode,
} from './types'
import type { DatabaseDiagnostics, SqliteDatabase, SyncEvent } from './sqliteDatabase'
import type { BrowserSqliteOpenConfig } from './browserSqliteProtocol'
import { buildBrowserSqliteHeadBlobId, buildBrowserSqliteHeadLane } from './browserSqliteProtocol'
import {
    openBrowserSqliteDatabase,
    shouldUseBrowserSqliteWorkerOnUiThread,
} from './browserSqliteDatabase'
import { openSqlite3MtLightDatabase } from './sqlite3MtLightDatabase'
import { SdkError } from './errors'
import { validatePath } from './pathUtils'
import { openDatabaseWithEngineFactory } from './realSqliteDatabase'
import {
    resolveWorkspaceSqliteEngineFactory,
    type WorkspaceSqliteEngineFactoryProvider,
} from './sqliteEngine'
import { createSqlite3MtLaneIndex, type Sqlite3MtLaneIndex, type Sqlite3MtLaneRecord } from './sqlite3mtLaneIndex'
import { sdkConsole } from './logger'

type WorkspaceObserverBridge = Workspace & {
    emitSdkObserverEvent?: (
        domain: string,
        level: 'debug' | 'info' | 'warn' | 'error',
        message: string,
        data?: unknown,
    ) => void
    emitSdkObserverMetric?: (
        name: string,
        value: number,
        tags?: Record<string, string>,
    ) => void
}

type WorkspacePolicyAwareSqliteDatabase = SqliteDatabase & {
    setWorkspaceSyncPolicy?: (policy: WorkspaceSyncPolicy) => Promise<void>
}

type SqliteDiagnosticsCapableDatabase = SqliteDatabase & {
    diagnostics?: () => DatabaseDiagnostics
    onDiagnostics?: (callback: (diag: DatabaseDiagnostics) => void) => () => void
}

const SQLITE_LANE_BACKGROUND_OPEN_LIMIT = 16

// ── Option types ──────────────────────────────────────────────────

// ReadTextOptions is re-exported from types.ts (single source of truth).
export type { ReadTextOptions } from './types'

/** Options for readBinaryBatch() (§80 §9.7). */
export interface ReadBinaryBatchOptions {
    /** Number of parallel reads. Default: 4. */
    concurrency?: number
    signal?: AbortSignal
    onProgress?: (done: number, total: number, path: string) => void
}

/** Options for importFiles() on session (§80 §9.9). */
export interface SessionImportOptions {
    signal?: AbortSignal
    onProgress?: (done: number, total: number) => void
}

export interface SessionImportResult {
    created: number
    replaced: number
    paths: string[]
    aborted?: boolean
}

/** File change event emitted by watch() (§80 §9.10). */
export interface FileChangeEvent {
    type: 'created' | 'modified' | 'deleted'
    path: string
    source: 'local' | 'sync' | 'merge'
}

export interface SearchOptions {
    pathPrefix?: string
    caseSensitive?: boolean
    maxMatches?: number
    signal?: AbortSignal
}

export interface SearchResult {
    matches: Array<{ path: string; line: number; col: number; snippet: string }>
    truncated: boolean
}

/**
 * Point-in-time diagnostic snapshot of a workspace session's health.
 * Captures sync state, open SQLite databases, recent observer events,
 * and recent errors — without secrets, tokens, or file content.
 *
 * Bounded: events cap at 50, errors cap at 20. Always safe to log/export.
 */
export interface WorkspaceDiagnosticSnapshot {
    /** Moment of capture (ISO 8601). */
    readonly capturedAt: string
    /** Basic session metadata. */
    readonly session: {
        readonly blobId: string
        readonly label: string
        readonly emoji: string
        readonly canWrite: boolean
        /** Session open duration in milliseconds. Zero if session is closed. */
        readonly sessionAgeMs: number
        readonly isOpen: boolean
    }
    /** Current sync state (direct read, not buffered). */
    readonly sync: {
        readonly syncing: boolean
        readonly synced: boolean
        readonly online: boolean
        readonly connectivity: string
        readonly pendingChanges: number
        readonly localHead: string | null
        readonly remoteHead: string | null
        /** Last sync error, redacted — no stack traces or sensitive detail. */
        readonly lastSyncError: { readonly code: string; readonly message: string } | null
        readonly lastSyncAt: number | null
    }
    /** Open SQLite databases with per-db diagnostics. */
    readonly sqliteDatabases: ReadonlyArray<{
        readonly path: string
        readonly isOpen: boolean
        readonly syncState: string
        readonly remoteEpoch: number
        readonly dirtyPageCount: number
    }>
    /** Recent warn/error observer events (capped at 50, newest first). */
    readonly recentEvents: ReadonlyArray<{
        readonly domain: string
        readonly level: 'warn' | 'error'
        readonly message: string
        readonly timestamp: number
    }>
    /** Recent errors captured during session operations (capped at 20, newest first). */
    readonly recentErrors: ReadonlyArray<{
        readonly code: string
        readonly message: string
        readonly timestamp: number
    }>
}

/**
 * Simplified SDK API for workspace operations (§80 §5.2).
 *
 * Provides guarded access to file operations, sync, history, and events.
 * All write operations throw `WRITE_FORBIDDEN` on read-only workspaces.
 * All operations throw `WORKSPACE_CLOSED` after `close()` is called.
 */
export interface WorkspaceSession {
    /** Unique blob identifier for this workspace. */
    readonly blobId: string
    /** Whether this session has write access. */
    readonly canWrite: boolean
    /** Human-readable workspace label. */
    readonly label: string
    /** Emoji icon for the workspace. */
    readonly emoji: string

    // ─── File operations ───

    /**
     * List the currently materialized file snapshot at a path.
     * In on-demand mode, prefer listDir() for complete lazy directory reads.
     */
    listFiles(path?: string, opts?: { recursive?: boolean }): FileStat[]

    /**
     * Lazy directory listing — returns immediate children (files + synthesized directories).
     * Preferred over listFiles for file browser UIs that need directory entries.
     */
    listDir(path: string): Promise<FileStat[]>

    /**
     * Streaming variant of listDir — yields FileStat entries one-by-one.
     * Backpressure-friendly for directories with 100k+ children.
     */
    listDirStream(path: string): AsyncGenerator<FileStat>

    /** Get file metadata. Returns `null` if not found. */
    stat(path: string): FileStat | null
    /** Read file as text. Supports `{charset: 'windows-1252'}` for legacy encodings. */
    readText(path: string, opts?: ReadTextOptions): Promise<string | null>
    /** Read file as raw bytes. */
    readBinary(path: string, signal?: AbortSignal): Promise<Uint8Array | null>

    /**
     * Read a byte range [offset, offset+length) from a file.
     * V3: only fetches+decrypts chunks that overlap the range.
     */
    readBytes(path: string, opts: { offset: number; length: number }, signal?: AbortSignal): Promise<Uint8Array | null>

    /** Batch-read multiple files in parallel (§80 §9.7). Yields `{data: null}` for read failures. */
    readBinaryBatch(
        paths: string[],
        opts?: ReadBinaryBatchOptions,
    ): AsyncGenerator<{ path: string; data: Uint8Array | null }>

    /**
     * V3 seekable streaming: chunk-by-chunk decryption with O(1) offset seeking.
     * Use `offset` for FUSE read(), HTTP Range, seekable media.
     * `length` limits total bytes yielded. `chunkSize` re-slices output chunks.
     */
    createReadStream(
        path: string,
        opts?: { offset?: number; length?: number; chunkSize?: number },
        signal?: AbortSignal,
    ): AsyncGenerator<Uint8Array>

    /** Write text file. Synchronous — triggers async commit+sync pipeline. */
    writeText(path: string, content: string): void
    /** Write binary file. Synchronous — triggers async commit+sync pipeline. */
    writeBytes(path: string, bytes: Uint8Array): void

    /**
     * Partial write: overwrite bytes at a specific offset.
     * V3: leverages CDC dedup — unchanged chunks are reused.
     */
    patchBytes(path: string, offset: number, data: Uint8Array, signal?: AbortSignal): Promise<void>

    /**
     * Append bytes to end of file.
     * V3: CDC chunk stability means existing chunks are reused.
     */
    appendBytes(path: string, data: Uint8Array, signal?: AbortSignal): Promise<void>

    /** Delete file or directory. Returns `true` if existed. */
    delete(path: string): boolean
    /** Rename/move a file. Returns `true` if source existed. */
    rename(fromPath: string, toPath: string): boolean
    /** Create directory (parents created automatically). */
    mkdir(path: string): void

    /** Create symbolic link. V3: first-class entry in Merkle directory tree. */
    symlink(path: string, target: string): void

    /** Update file metadata without re-writing content. V3: only dir entry re-encrypted. */
    setMeta(path: string, meta: Record<string, unknown>): void

    /** Batch-import files with concurrency + progress (§80 §9.9). */
    importFiles(
        files: Array<{ path: string; data: Uint8Array }>,
        opts?: SessionImportOptions,
    ): Promise<SessionImportResult>

    /** Full-text search across all text files with parallel decryption. */
    searchText(query: string, opts?: SearchOptions): Promise<SearchResult>

    // ─── Reactive ───

    /** Watch file changes at a path. Returns unsubscribe function (§80 §9.10). */
    watch(
        path: string,
        callback: (event: FileChangeEvent) => void,
        opts?: { recursive?: boolean },
    ): () => void

    // ─── Sync ───

    /**
     * Trigger immediate on-demand sync with server without forcing full
     * workspace materialization.
     */
    syncOnDemand?(): Promise<void>
    /** Trigger immediate sync with server. */
    syncNow(): Promise<void>
    /** Current sync status (read-only). */
    readonly syncState: SyncState

    /** Acquire an opaque, resource-scoped lease within this workspace blob. */
    acquireResourceLease(
        resourceName: string,
        options: { mode?: 0 | 1 | 2; holderId: string; ttlSeconds?: number },
    ): Promise<WorkspaceResourceLease | null>
    /** Refresh a previously acquired workspace resource lease. */
    refreshResourceLease(leaseId: string, ttlSeconds?: number): Promise<WorkspaceResourceLease | null>
    /** Release a previously acquired workspace resource lease. */
    releaseResourceLease(leaseId: string): Promise<boolean>

    // ─── History ───

    /** Get commit log. Use `pathFilter` to filter by file. */
    getCommitLog(opts?: { maxCommits?: number; pathFilter?: string }): CommitSummary[]
    /** List direct child entries for a directory as it existed at a specific commit. */
    listDirAtCommit(path: string, commitHash: string): Promise<FileStat[]>
    /** Read file content at a specific historical commit. */
    readTextAtCommit(path: string, commitHash: string, opts?: ReadTextOptions): Promise<string | null>
    /** Get line-by-line attribution for a file. */
    blame(path: string, signal?: AbortSignal): Promise<BlameLine[]>

    // ─── Events ───

    /** Subscribe to workspace events. Returns unsubscribe function. */
    on<E extends WorkspaceEventType>(
        event: E,
        fn: (payload: WorkspaceEventPayload[E]) => void,
    ): () => void

    // ─── SQLite (§95) ───

    /**
     * Open a SQLite database with high-level SQL API.
     * Bridges page-level sync (RealWorkspace.openSqliteDb) with SqliteDatabase interface.
     * `syncIntervalMs` sets the background sqlite3mt remote-head poll cadence
     * used when workspace sync policy starts this database automatically.
     * Tab close → db.close() → all connections closed → queries aborted → WASM freed.
     */
    openSqlite(path: string, options?: OpenSqliteOptions): Promise<SqliteDatabase>

    /**
     * Create a serializable sqlite3mt/browser open config for component-job and
     * worker control-plane clients. This is an on-demand workspace auth surface;
     * callers must not force a full workspace sync before using it.
     */
    createBrowserSqliteOpenConfig(path: string): BrowserSqliteOpenConfig

    /**
     * Close a SQLite database through the session's serialization gate.
     * Must be used instead of direct db.close() to prevent race conditions
     * between close() and a subsequent openSqlite() (WASM memory corruption).
     */
    closeSqliteDb(db: SqliteDatabase): void

    // ─── Diagnostics (§290) ───

    /**
     * Capture a point-in-time snapshot of session health.
     * Fast (no network calls), bounded (ring buffers capped at 50 events / 20 errors),
     * and redacted by default (no secrets, tokens, or file content).
     *
     * Complements the observer event stream — answers "how is it right now?"
     * without requiring callers to aggregate data from multiple sources.
     */
    getDiagnosticSnapshot(): WorkspaceDiagnosticSnapshot

    // ─── Lifecycle ───

    /** Close the session. Idempotent — safe to call multiple times. */
    close(): void
}

// ── MIME helpers ──────────────────────────────────────────────────

const TEXT_MIMES = new Set([
    'application/json', 'application/xml', 'application/javascript',
    'application/typescript', 'application/x-yaml', 'application/toml',
])

function isSearchableMime(mime: string): boolean {
    if (mime.startsWith('text/')) return true
    return TEXT_MIMES.has(mime)
}

// ── Path matching helpers ────────────────────────────────────────

function isChildPath(filePath: string, watchPath: string, recursive: boolean): boolean {
    const prefix = watchPath === '/' ? '/' : watchPath.replace(/\/+$/, '') + '/'
    if (!filePath.startsWith(prefix) && filePath !== watchPath) return false
    if (recursive) return true
    // Non-recursive: only direct children
    const relative = prefix === '/' ? filePath.slice(1) : filePath.slice(prefix.length)
    return relative.length > 0 && !relative.includes('/')
}

// ── Implementation ────────────────────────────────────────────────

const BATCH_CONCURRENCY = 4
const SEARCH_CONCURRENCY = 8

interface BrowserSqliteOpenConfigProvider {
    createBrowserSqliteOpenConfig(path: string): BrowserSqliteOpenConfig
}

interface Sqlite3MtSyncRegistrationProvider {
    registerSqlite3MtSyncCallback(headBlobId: string, triggerSync: () => void): () => void
}

function hasBrowserSqliteOpenConfigProvider(
    workspace: Workspace
): workspace is Workspace & BrowserSqliteOpenConfigProvider {
    return typeof (workspace as Partial<BrowserSqliteOpenConfigProvider>).createBrowserSqliteOpenConfig === 'function'
}

function hasSqlite3MtSyncRegistrationProvider(
    workspace: Workspace
): workspace is Workspace & Sqlite3MtSyncRegistrationProvider {
    return typeof (workspace as Partial<Sqlite3MtSyncRegistrationProvider>).registerSqlite3MtSyncCallback === 'function'
}

/**
 * RealWorkspaceSession — delegates to a full Workspace with guards.
 */
export class RealWorkspaceSession implements WorkspaceSession {
    private closed = false
    /** P4: Cached listFiles results — invalidated on any mutation or external change. */
    private listCache = new Map<string, FileStat[]>()
    /** P4: Unsubscribe from fs:external-change — stored for cleanup in close(). */
    private readonly unsubExternalChange: () => void
    private readonly unsubStateChange: () => void

    /** §290: Session open timestamp — used for sessionAgeMs in diagnostic snapshot. */
    private readonly sessionOpenedAt = Date.now()
    private lastAppliedWorkspaceSyncPolicy: WorkspaceSyncPolicy | null = null
    private readonly sqliteLaneIndex: Sqlite3MtLaneIndex
    private sqliteLaneRecoveryScheduled = false
    private sqliteLaneRecoveryPolicy: WorkspaceSyncPolicy | null = null
    private readonly sqliteLaneRecoveryInFlight = new Set<string>()

    /** §290: Ring buffer — capped at 50 most recent warn/error observer events. */
    private _recentEvents: Array<{
        domain: string
        level: 'warn' | 'error'
        message: string
        timestamp: number
    }> = []
    /** §290: Ring buffer — capped at 20 most recent errors. */
    private _recentErrors: Array<{
        code: string
        message: string
        timestamp: number
    }> = []

    constructor(private readonly workspace: Workspace) {
        this.sqliteLaneIndex = createSqlite3MtLaneIndex(workspace.blobId)
        // P4: Invalidate cache when external sync brings in file changes
        this.unsubExternalChange = this.workspace.on('fs:external-change', () => this.invalidateListCache())
        this.unsubStateChange = this.workspace.on('state:changed', (state) => {
            const policy = state.syncPolicy ?? (state.syncPaused ? 'paused' : 'on-demand')
            void this.applyWorkspaceSyncPolicyToOpenSqliteDatabases(
                policy,
            )
            this.scheduleIndexedSqliteLaneRecovery(policy)
        })
        this.scheduleIndexedSqliteLaneRecovery(this.currentWorkspaceSyncPolicy())
    }

    get blobId(): string { return this.workspace.blobId }
    get canWrite(): boolean { return this.workspace.canWrite }
    get label(): string { return this.workspace.label }
    get emoji(): string { return this.workspace.emoji }

    /** P4: Clear cached listFiles results on any write or external change. */
    private invalidateListCache(): void { this.listCache.clear() }

    // ── File operations ──────────────────────────────────────────

    listFiles(path?: string, opts?: { recursive?: boolean }): FileStat[] {
        this.ensureOpen()
        const cacheKey = `${path ?? '/'}:${opts?.recursive ? 'r' : 'd'}`

        // P4: Return cached frozen result if available
        const cached = this.listCache.get(cacheKey)
        if (cached) return cached

        // workspace.fs.list() returns the currently materialized snapshot recursively.
        const allFiles = this.workspace.fs.list(path)
        let result: FileStat[]
        if (opts?.recursive) {
            result = allFiles
        } else {
            // Non-recursive (default): filter to direct children only
            const prefix = path && path !== '/' ? path.replace(/\/+$/, '') + '/' : '/'
            result = allFiles.filter(f => {
                const relative = prefix === '/' ? f.path.slice(1) : f.path.slice(prefix.length)
                return relative.length > 0 && !relative.includes('/')
            })
        }

        // P4: Freeze each FileStat entry and the result array
        for (const entry of result) Object.freeze(entry)
        Object.freeze(result)

        this.listCache.set(cacheKey, result)
        return result
    }

    async listDir(path: string): Promise<FileStat[]> {
        this.ensureOpen()
        const entries = await this.workspace.fs.listDirAsync(path)
        for (const entry of entries) Object.freeze(entry)
        Object.freeze(entries)
        return entries
    }

    async *listDirStream(path: string): AsyncGenerator<FileStat> {
        this.ensureOpen()
        for await (const entry of this.workspace.fs.listDirStream(path)) {
            Object.freeze(entry)
            yield entry
        }
    }

    stat(path: string): FileStat | null {
        this.ensureOpen()
        const entry = this.workspace.fs.stat(path)
        // P4: Freeze stat results too
        if (entry) Object.freeze(entry)
        return entry
    }

    async readText(path: string, opts?: ReadTextOptions): Promise<string | null> {
        this.ensureOpen()
        if (opts?.signal?.aborted) throw new DOMException('Aborted', 'AbortError')
        if (opts?.charset) {
            // Charset mode: read raw bytes → decode with specified charset
            const bytes = await this.workspace.fs.readBinary(path)
            if (!bytes) return null
            try {
                return new TextDecoder(opts.charset, { fatal: true }).decode(bytes)
            } catch (err) {
                throw new SdkError('SDK_INVALID_CHARSET', `Unsupported or invalid charset: ${opts.charset}`, { cause: err })
            }
        }
        return this.workspace.fs.readText(path)
    }

    async readBinary(path: string, signal?: AbortSignal): Promise<Uint8Array | null> {
        this.ensureOpen()
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
        return this.workspace.fs.readBinary(path)
    }

    async *readBinaryBatch(
        paths: string[],
        opts?: ReadBinaryBatchOptions,
    ): AsyncGenerator<{ path: string; data: Uint8Array | null }> {
        this.ensureOpen()
        const concurrency = Math.max(1, opts?.concurrency ?? BATCH_CONCURRENCY)
        let done = 0

        for (let i = 0; i < paths.length; i += concurrency) {
            if (opts?.signal?.aborted) throw new DOMException('Aborted', 'AbortError')
            const batch = paths.slice(i, i + concurrency)
            const results = await Promise.all(
                batch.map(async p => {
                    try { return { path: p, data: await this.readBinary(p) } }
                    catch { return { path: p, data: null as Uint8Array | null } }
                })
            )
            for (const r of results) {
                yield r
                done++
                opts?.onProgress?.(done, paths.length, r.path)
            }
        }
    }

    async readBytes(path: string, opts: { offset: number; length: number }, signal?: AbortSignal): Promise<Uint8Array | null> {
        this.ensureOpen()
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
        return this.workspace.fs.readBytes(path, opts, signal)
    }

    async *createReadStream(
        path: string,
        opts?: { offset?: number; length?: number; chunkSize?: number },
        signal?: AbortSignal,
    ): AsyncGenerator<Uint8Array> {
        this.ensureOpen()
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
        yield* this.workspace.fs.createByteStream(path, opts, signal)
    }

    writeText(path: string, content: string): void {
        this.ensureOpen()
        this.ensureWrite()
        validatePath(path)
        this.workspace.fs.writeText(path, content)
        this.invalidateListCache()
    }

    writeBytes(path: string, bytes: Uint8Array): void {
        this.ensureOpen()
        this.ensureWrite()
        validatePath(path)
        this.workspace.fs.writeBytes(path, bytes)
        this.invalidateListCache()
    }

    delete(path: string): boolean {
        this.ensureOpen()
        this.ensureWrite()
        validatePath(path)
        const result = this.workspace.fs.delete(path)
        this.invalidateListCache()
        return result
    }

    rename(fromPath: string, toPath: string): boolean {
        this.ensureOpen()
        this.ensureWrite()
        validatePath(fromPath)
        validatePath(toPath)
        const result = this.workspace.fs.rename(fromPath, toPath)
        this.invalidateListCache()
        return result
    }

    mkdir(path: string): void {
        this.ensureOpen()
        this.ensureWrite()
        validatePath(path)
        this.workspace.fs.mkdir(path)
        this.invalidateListCache()
    }

    async patchBytes(path: string, offset: number, data: Uint8Array, signal?: AbortSignal): Promise<void> {
        this.ensureOpen()
        this.ensureWrite()
        validatePath(path)
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
        await this.workspace.fs.patchBytes(path, offset, data, signal)
        this.invalidateListCache()
    }

    async appendBytes(path: string, data: Uint8Array, signal?: AbortSignal): Promise<void> {
        this.ensureOpen()
        this.ensureWrite()
        validatePath(path)
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
        await this.workspace.fs.appendBytes(path, data, signal)
        this.invalidateListCache()
    }

    symlink(path: string, target: string): void {
        this.ensureOpen()
        this.ensureWrite()
        validatePath(path)
        // target is NOT validated — it's a reference string, not a workspace path
        this.workspace.fs.symlink(path, target)
        this.invalidateListCache()
    }

    setMeta(path: string, meta: Record<string, unknown>): void {
        this.ensureOpen()
        this.ensureWrite()
        validatePath(path)
        this.workspace.fs.setMeta(path, meta)
        this.invalidateListCache()
    }

    async importFiles(
        files: Array<{ path: string; data: Uint8Array }>,
        opts?: SessionImportOptions,
    ): Promise<SessionImportResult> {
        this.ensureOpen()
        this.ensureWrite()
        for (const f of files) {
            validatePath(f.path)
        }
        if (!this.workspace.fs.importFiles) {
            // Fallback: write one-by-one if engine doesn't expose importFiles
            let created = 0
            const paths: string[] = []
            for (const f of files) {
                if (opts?.signal?.aborted) return { created, replaced: 0, paths, aborted: true }
                this.workspace.fs.writeBytes(f.path, f.data)
                paths.push(f.path)
                created++
                opts?.onProgress?.(created, files.length)
            }
            this.invalidateListCache()
            return { created, replaced: 0, paths }
        }
        // Map session format → engine format
        const entries: ImportFileEntry[] = files.map(f => ({ path: f.path, content: f.data }))
        const engineOpts: EngineImportOpts = {
            signal: opts?.signal,
            onProgress: opts?.onProgress
                ? (p) => opts.onProgress!(p.current, p.total)
                : undefined,
        }
        const result: EngineImportResult = await this.workspace.fs.importFiles(entries, engineOpts)
        this.invalidateListCache()
        return {
            created: result.created,
            replaced: result.replaced,
            paths: result.paths,
            aborted: result.aborted,
        }
    }

    // ── Search (P5: parallel decryption) ─────────────────────────

    async searchText(query: string, opts?: SearchOptions): Promise<SearchResult> {
        this.ensureOpen()
        if (opts?.signal?.aborted) throw new DOMException('Aborted', 'AbortError')
        if (query.length === 0) return { matches: [], truncated: false }

        const allFiles = this.workspace.fs.list(opts?.pathPrefix)
            .filter(f => f.kind === 'file' && isSearchableMime(f.mime))
        const maxMatches = opts?.maxMatches ?? 200
        const caseSensitive = opts?.caseSensitive ?? false
        const matches: SearchResult['matches'] = []
        let truncated = false

        const normalizedQuery = caseSensitive ? query : query.toLowerCase()

        // P5: Parallel decryption in batches of SEARCH_CONCURRENCY
        for (let i = 0; i < allFiles.length && !truncated; i += SEARCH_CONCURRENCY) {
            if (opts?.signal?.aborted) throw new DOMException('Aborted', 'AbortError')
            const batch = allFiles.slice(i, i + SEARCH_CONCURRENCY)
            const contents = await Promise.all(
                batch.map(async f => {
                    try { return { path: f.path, text: await this.workspace.fs.readText(f.path) } }
                    catch { return { path: f.path, text: null as string | null } }
                })
            )

            for (const { path: filePath, text } of contents) {
                if (!text || truncated) continue

                const lines = text.split('\n')
                for (let li = 0; li < lines.length; li++) {
                    if (matches.length >= maxMatches) { truncated = true; break }
                    const line = lines[li]
                    const searchLine = caseSensitive ? line : line.toLowerCase()
                    let col = searchLine.indexOf(normalizedQuery)
                    while (col !== -1) {
                        if (matches.length >= maxMatches) { truncated = true; break }
                        matches.push({
                            path: filePath,
                            line: li + 1,
                            col: col + 1,
                            snippet: line.length > 200
                                ? line.slice(Math.max(0, col - 50), col + 150)
                                : line,
                        })
                        col = searchLine.indexOf(normalizedQuery, col + normalizedQuery.length)
                    }
                    if (truncated) break
                }
            }
        }

        return { matches, truncated }
    }

    // ── Watch (P6) ───────────────────────────────────────────────

    watch(
        path: string,
        callback: (event: FileChangeEvent) => void,
        opts?: { recursive?: boolean },
    ): () => void {
        this.ensureOpen()
        const recursive = opts?.recursive ?? false

        // Snapshot current state for type detection
        const knownPaths = new Set(
            this.workspace.fs.list('/').map(f => f.path)
        )

        const handleLocal = (payload: FsChangeEvent) => {
            for (const p of payload.paths) {
                if (!isChildPath(p, path, recursive)) continue
                const stat = this.workspace.fs.stat(p)
                if (stat && !knownPaths.has(p)) {
                    knownPaths.add(p)
                    callback({ type: 'created', path: p, source: 'local' })
                } else if (stat && knownPaths.has(p)) {
                    callback({ type: 'modified', path: p, source: 'local' })
                } else if (!stat && knownPaths.has(p)) {
                    knownPaths.delete(p)
                    callback({ type: 'deleted', path: p, source: 'local' })
                }
            }
        }

        const handleExternal = (payload: FsExternalChangeEvent) => {
            const source: FileChangeEvent['source'] =
                payload.source === 'merge' ? 'merge' : 'sync'
            for (const p of payload.paths) {
                if (!isChildPath(p, path, recursive)) continue
                const stat = this.workspace.fs.stat(p)
                if (stat && !knownPaths.has(p)) {
                    knownPaths.add(p)
                    callback({ type: 'created', path: p, source })
                } else if (stat && knownPaths.has(p)) {
                    callback({ type: 'modified', path: p, source })
                } else if (!stat && knownPaths.has(p)) {
                    knownPaths.delete(p)
                    callback({ type: 'deleted', path: p, source })
                }
            }
        }

        const unsubLocal = this.workspace.on('fs:changed', handleLocal)
        const unsubExternal = this.workspace.on('fs:external-change', handleExternal)

        return () => {
            unsubLocal()
            unsubExternal()
        }
    }

    // ── Sync ─────────────────────────────────────────────────────

    async syncOnDemand(): Promise<void> {
        this.ensureOpen()
        try {
            const syncOnDemand = this.workspace.syncOnDemand
            if (typeof syncOnDemand === 'function') {
                return await syncOnDemand.call(this.workspace)
            }
            return await this.workspace.syncNow()
        } catch (err) {
            // §290: Capture sync error for diagnostic snapshot
            if (err instanceof SdkError) this.captureError(err.code, err.message)
            else if (err instanceof Error) this.captureError('SYNC_FAILED', err.message)
            else this.captureError('SYNC_FAILED', String(err))
            throw err
        }
    }

    async syncNow(): Promise<void> {
        this.ensureOpen()
        try {
            return await this.workspace.syncNow()
        } catch (err) {
            // §290: Capture sync error for diagnostic snapshot
            if (err instanceof SdkError) this.captureError(err.code, err.message)
            else if (err instanceof Error) this.captureError('SYNC_FAILED', err.message)
            else this.captureError('SYNC_FAILED', String(err))
            throw err
        }
    }

    get syncState(): SyncState {
        this.ensureOpen()
        return this.workspace.state
    }

    async acquireResourceLease(
        resourceName: string,
        options: { mode?: 0 | 1 | 2; holderId: string; ttlSeconds?: number },
    ): Promise<WorkspaceResourceLease | null> {
        this.ensureOpen()
        return this.workspace.acquireResourceLease(resourceName, options)
    }

    async refreshResourceLease(leaseId: string, ttlSeconds?: number): Promise<WorkspaceResourceLease | null> {
        this.ensureOpen()
        return this.workspace.refreshResourceLease(leaseId, ttlSeconds)
    }

    async releaseResourceLease(leaseId: string): Promise<boolean> {
        this.ensureOpen()
        return this.workspace.releaseResourceLease(leaseId)
    }

    // ── History ──────────────────────────────────────────────────

    getCommitLog(opts?: { maxCommits?: number; pathFilter?: string }): CommitSummary[] {
        this.ensureOpen()
        if (opts?.pathFilter) {
            return this.workspace.history.listForFile(opts.pathFilter, { limit: opts.maxCommits })
        }
        return this.workspace.history.list({ limit: opts?.maxCommits })
    }

    async listDirAtCommit(path: string, commitHash: string): Promise<FileStat[]> {
        this.ensureOpen()
        const entries = await this.workspace.history.listDirAtCommit(path, commitHash)
        for (const entry of entries) Object.freeze(entry)
        Object.freeze(entries)
        return entries
    }

    async readTextAtCommit(path: string, commitHash: string, opts?: ReadTextOptions): Promise<string | null> {
        this.ensureOpen()
        if (opts?.signal?.aborted) throw new DOMException('Aborted', 'AbortError')
        if (opts?.charset) {
            const bytes = await this.workspace.history.readBytesAtCommit(path, commitHash)
            if (!bytes) return null
            try {
                return new TextDecoder(opts.charset, { fatal: true }).decode(bytes)
            } catch (err) {
                throw new SdkError('SDK_INVALID_CHARSET', `Unsupported or invalid charset: ${opts.charset}`, { cause: err })
            }
        }
        return this.workspace.history.readTextAtCommit(path, commitHash)
    }

    async blame(path: string, signal?: AbortSignal): Promise<BlameLine[]> {
        this.ensureOpen()
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
        return this.workspace.history.blame(path, signal)
    }

    // ── Events ───────────────────────────────────────────────────

    on<E extends WorkspaceEventType>(
        event: E,
        fn: (payload: WorkspaceEventPayload[E]) => void,
    ): () => void {
        this.ensureOpen()
        return this.workspace.on(event, fn)
    }

    // ── SQLite (§95) ─────────────────────────────────────────────

    /** Track open SQLite databases for cleanup on session close. */
    private openDatabases: SqliteDatabase[] = []
    /** Unsubscribe functions for SQLite sync listeners. */
    private _sqliteUnsubFns: (() => void)[] = []
    /**
     * Database cache: path → open SqliteDatabase.
     * WASM dbs stay alive for the session lifetime. This eliminates:
     *   1. WASM function table corruption on VFS re-registration
     *   2. close/open race conditions
     *   3. React StrictMode double-mount issues
     * Databases are only truly closed in session.close().
     */
    private _sqliteCache = new Map<string, SqliteDatabase>()
    private _sqliteSyncIntervals = new WeakMap<SqliteDatabase, number>()
    private _sqlitePaths = new WeakMap<SqliteDatabase, string>()
    private _sqliteSyncNowInFlight = new WeakMap<SqliteDatabase, Promise<void>>()
    private _sqliteBackgroundSyncModes = new WeakMap<SqliteDatabase, SqliteBackgroundSyncMode>()
    /**
     * Async mutex for SQLite WASM operations (serializes first-open of each db).
     */
    private _sqliteMutex: Promise<void> = Promise.resolve()

    private currentWorkspaceSyncPolicy(): WorkspaceSyncPolicy {
        const state = (this.workspace as Partial<Workspace>).state
        const explicitPolicy = state?.syncPolicy
        if (explicitPolicy) return explicitPolicy

        const getSyncPolicy = (this.workspace as Partial<Workspace>).getSyncPolicy
        if (typeof getSyncPolicy === 'function') {
            try {
                return getSyncPolicy.call(this.workspace)
            } catch {
                // Fall back to state/default below; policy lookup is best-effort for legacy adapters.
            }
        }

        return state?.syncPaused ? 'paused' : 'on-demand'
    }

    private sqliteLaneIdentity(path: string) {
        return {
            blobId: this.blobId,
            path,
            headBlobId: buildBrowserSqliteHeadBlobId(this.blobId, path),
            headLane: buildBrowserSqliteHeadLane(this.blobId, path),
        }
    }

    private markSqliteLaneKnown(path: string): void {
        this.sqliteLaneIndex.markKnown(this.sqliteLaneIdentity(path))
    }

    private markSqliteLaneDirtyFromDiagnostics(path: string, diag: DatabaseDiagnostics): void {
        const hasPendingPush = diag.sync.pendingPushPages > 0
            || diag.memory.dirtyPageCount > 0
            || diag.sync.localEpoch > diag.sync.remoteEpoch

        if (hasPendingPush) {
            this.sqliteLaneIndex.markDirty(path)
            return
        }

        if (!diag.sync.isSyncing && diag.sync.lastError == null) {
            this.sqliteLaneIndex.markClean(path)
        }
    }

    private refreshSqliteLaneDirtyState(path: string, db: SqliteDatabase): void {
        const diagnostics = (db as SqliteDiagnosticsCapableDatabase).diagnostics
        if (typeof diagnostics !== 'function') return
        try {
            this.markSqliteLaneDirtyFromDiagnostics(path, diagnostics.call(db))
        } catch {
            // Diagnostics are best-effort metadata for lane recovery.
        }
    }

    private trackSqliteLaneIndex(path: string, db: SqliteDatabase): () => void {
        this.markSqliteLaneKnown(path)

        const unsubscribers: Array<() => void> = []
        const diagnosticsCapable = db as SqliteDiagnosticsCapableDatabase
        if (typeof diagnosticsCapable.onDiagnostics === 'function') {
            try {
                unsubscribers.push(diagnosticsCapable.onDiagnostics(diag => {
                    this.markSqliteLaneDirtyFromDiagnostics(path, diag)
                }))
            } catch {
                // Keep SQLite usable even if an engine cannot expose diagnostics.
            }
        }

        if (typeof db.onSync === 'function') {
            try {
                unsubscribers.push(db.onSync((event: SyncEvent) => {
                    if (event.type === 'sync-error') {
                        this.refreshSqliteLaneDirtyState(path, db)
                        return
                    }
                    if (event.type === 'sync-complete') {
                        this.refreshSqliteLaneDirtyState(path, db)
                    }
                }))
            } catch {
                // Sync event wiring is best effort; policy application still runs.
            }
        }

        this.refreshSqliteLaneDirtyState(path, db)

        return () => {
            for (const unsubscribe of unsubscribers.splice(0)) {
                try { unsubscribe() } catch { /* cleanup best-effort */ }
            }
        }
    }

    private scheduleIndexedSqliteLaneRecovery(policy: WorkspaceSyncPolicy): void {
        if (this.closed) return
        if (policy !== 'auto') {
            this.sqliteLaneRecoveryPolicy = null
            return
        }
        this.sqliteLaneRecoveryPolicy = policy
        if (this.sqliteLaneRecoveryScheduled) return
        this.sqliteLaneRecoveryScheduled = true
        queueMicrotask(() => {
            this.sqliteLaneRecoveryScheduled = false
            const recoveryPolicy = this.sqliteLaneRecoveryPolicy
            this.sqliteLaneRecoveryPolicy = null
            if (this.closed || recoveryPolicy == null) return
            void this.recoverIndexedSqliteLanes(recoveryPolicy).catch(error => {
                this.emitSdkObserverEvent('sqlite', 'warn', 'Indexed SQLite lane recovery failed.', {
                    blobId: this.blobId,
                    policy: recoveryPolicy,
                    error: error instanceof Error ? error.message : String(error),
                })
            })
        })
    }

    private async recoverIndexedSqliteLanes(policy: WorkspaceSyncPolicy): Promise<void> {
        if (this.closed || policy !== 'auto') return
        const records = this.selectIndexedSqliteLanesForPolicy(policy)

        for (const record of records) {
            if (this.closed || this.currentWorkspaceSyncPolicy() !== 'auto') return
            if (this._sqliteCache.get(record.path)?.isOpen) continue
            if (this.sqliteLaneRecoveryInFlight.has(record.path)) continue

            this.sqliteLaneRecoveryInFlight.add(record.path)
            try {
                const db = await this.openSqlite(record.path)
                this.refreshSqliteLaneDirtyState(record.path, db)
            } catch (error) {
                this.emitSdkObserverEvent('sqlite', 'warn', 'Failed to recover indexed SQLite lane.', {
                    blobId: this.blobId,
                    path: record.path,
                    policy,
                    dirty: record.dirty,
                    error: error instanceof Error ? error.message : String(error),
                })
            } finally {
                this.sqliteLaneRecoveryInFlight.delete(record.path)
            }
        }
    }

    private selectIndexedSqliteLanesForPolicy(policy: WorkspaceSyncPolicy): Sqlite3MtLaneRecord[] {
        const lanes = policy === 'auto'
            ? this.sqliteLaneIndex.list()
            : []

        return lanes.slice(0, SQLITE_LANE_BACKGROUND_OPEN_LIMIT)
    }

    /**
     * Mark a SQLite database as "not in use" by the current component.
     * The db is NOT closed — it stays cached for instant reuse on re-mount.
     * Only session.close() actually closes WASM resources.
     */
    closeSqliteDb(_db: SqliteDatabase): void {
        // No-op: db stays alive in cache. Closing the WASM db and re-opening
        // it causes "Out of bounds memory access" / "call_indirect signature
        // mismatch" due to sqlite3-wasm function table corruption on
        // re-registration. Cache-and-reuse is the correct strategy.
        sdkConsole.trace('[Session] closeSqliteDb: no-op (db stays cached)')
    }

    createBrowserSqliteOpenConfig(path: string): BrowserSqliteOpenConfig {
        return this.requireBrowserSqliteOpenConfig(path)
    }

    private scheduleSqliteSyncNow(path: string, db: SqliteDatabase, reason: 'policy' | 'head'): void {
        if (!db.isOpen || typeof db.syncNow !== 'function') {
            return
        }
        if (this._sqliteSyncNowInFlight.has(db)) {
            return
        }

        const run = (async () => {
            await db.syncNow()
        })()
            .catch(err => {
                sdkConsole.warn('[Session] sqlite3mt syncNow failed', {
                    path,
                    reason,
                    error: err instanceof Error ? err.message : String(err),
                })
            })
            .finally(() => {
                if (this._sqliteSyncNowInFlight.get(db) === run) {
                    this._sqliteSyncNowInFlight.delete(db)
                }
            })

        this._sqliteSyncNowInFlight.set(db, run)
    }

    private async applyWorkspaceSyncPolicyToOpenSqliteDatabases(policy: WorkspaceSyncPolicy, force = false): Promise<void> {
        if (!force && this.lastAppliedWorkspaceSyncPolicy === policy) return
        this.lastAppliedWorkspaceSyncPolicy = policy
        const databases = [...this.openDatabases]
        await Promise.allSettled(databases.map(async db => {
            if (this._sqliteBackgroundSyncModes.get(db) === 'manual') {
                await db.stopSync?.()
                return
            }
            const policyAware = db as WorkspacePolicyAwareSqliteDatabase
            await policyAware.setWorkspaceSyncPolicy?.(policy)
            if (policy === 'paused') {
                await db.stopSync?.()
                return
            }
            if (policy === 'on-demand') {
                await db.stopSync?.()
                return
            }
            await db.startSync?.({ interval: this._sqliteSyncIntervals.get(db) })
            this.scheduleSqliteSyncNow(this._sqlitePaths.get(db) ?? '(unknown)', db, 'policy')
        }))
    }

    private applyWorkspaceSyncPolicyToOpenSqliteDatabasesInBackground(policy: WorkspaceSyncPolicy, force = false): void {
        void this.applyWorkspaceSyncPolicyToOpenSqliteDatabases(policy, force).catch(err => {
            sdkConsole.warn('[Session] sqlite3mt background sync policy application failed', {
                policy,
                error: err instanceof Error ? err.message : String(err),
            })
        })
    }

    private requireBrowserSqliteOpenConfig(path: string): BrowserSqliteOpenConfig {
        if (!hasBrowserSqliteOpenConfigProvider(this.workspace)) {
            throw new Error('Browser sqlite worker requires a workspace that can provide serializable sqlite config; UI-thread fallback is forbidden.')
        }

        return this.workspace.createBrowserSqliteOpenConfig(path)
    }

    private registerBrowserSqliteHeadSync(path: string, db: SqliteDatabase): () => void {
        if (!hasSqlite3MtSyncRegistrationProvider(this.workspace)) {
            return () => {}
        }

        const headBlobId = buildBrowserSqliteHeadBlobId(this.blobId, path)
        sdkConsole.debug('[Session] registerBrowserSqliteHeadSync', { path, headBlobId })
        return this.workspace.registerSqlite3MtSyncCallback(headBlobId, () => {
            if (this.currentWorkspaceSyncPolicy() !== 'auto') {
                return
            }
            sdkConsole.debug('[Session] sqlite3mt head-triggered sync', { path, headBlobId })
            this.scheduleSqliteSyncNow(path, db, 'head')
        })
    }

    private trackOpenSqliteDatabase(path: string, db: SqliteDatabase, unregisterSync: () => void): SqliteDatabase {
        const originalClose = db.close.bind(db)
        const unregisterLaneIndex = this.trackSqliteLaneIndex(path, db)
        let closed = false

        db.close = async () => {
            if (closed) return
            closed = true
            try {
                await originalClose()
            } finally {
                unregisterSync()
                unregisterLaneIndex()
                this._sqliteCache.delete(path)
                this._sqlitePaths.delete(db)
                this._sqliteSyncNowInFlight.delete(db)
                this._sqliteBackgroundSyncModes.delete(db)
                if (!this.closed) {
                    this.openDatabases = this.openDatabases.filter(candidate => candidate !== db)
                }
            }
        }

        this.openDatabases.push(db)
        this._sqliteCache.set(path, db)
        this._sqlitePaths.set(db, path)
        return db
    }

    async openSqlite(path: string, options?: OpenSqliteOptions): Promise<SqliteDatabase> {
        this.ensureOpen()
        const startedAt = Date.now()
        const backgroundSync = options?.backgroundSync ?? 'workspace-policy'

        // Return cached db if still open — instant, no WASM allocation
        const cached = this._sqliteCache.get(path)
        if (cached && cached.isOpen) {
            if (options?.syncIntervalMs != null) {
                this._sqliteSyncIntervals.set(cached, options.syncIntervalMs)
            }
            this._sqliteBackgroundSyncModes.set(cached, backgroundSync)
            sdkConsole.trace('[Session] openSqlite: returning cached db for', path)
            this.applyWorkspaceSyncPolicyToOpenSqliteDatabasesInBackground(
                this.workspace.state.syncPolicy ?? (this.workspace.state.syncPaused ? 'paused' : 'on-demand'),
                true,
            )
            this.emitSdkObserverEvent('sqlite', 'debug', 'Reused cached SQLite database handle.', {
                blobId: this.blobId,
                path,
                cacheHit: true,
            })
            this.emitSdkObserverMetric('sdk.sqlite.open.duration_ms', 0, {
                blobId: this.blobId,
                cacheHit: 'true',
            })
            return cached
        }

        // Acquire mutex — serializes first-open (only needed for cold opens)
        let releaseLock!: () => void
        const myLock = new Promise<void>(r => releaseLock = r)
        const prevLock = this._sqliteMutex
        this._sqliteMutex = myLock

        sdkConsole.trace('[Session] openSqlite: awaiting mutex (cold open)...')
        await prevLock
        sdkConsole.trace('[Session] openSqlite: mutex acquired, opening', path)

        // Abort check (React StrictMode double-mount)
        if (options?.signal?.aborted) {
            sdkConsole.trace('[Session] openSqlite: ABORTED before open, releasing mutex')
            releaseLock()
            throw new DOMException('openSqlite aborted', 'AbortError')
        }

        // Double-check cache after mutex (another call may have cached it)
        const cachedAfterMutex = this._sqliteCache.get(path)
        if (cachedAfterMutex && cachedAfterMutex.isOpen) {
            if (options?.syncIntervalMs != null) {
                this._sqliteSyncIntervals.set(cachedAfterMutex, options.syncIntervalMs)
            }
            this._sqliteBackgroundSyncModes.set(cachedAfterMutex, backgroundSync)
            sdkConsole.trace('[Session] openSqlite: found cached db after mutex for', path)
            releaseLock()
            this.applyWorkspaceSyncPolicyToOpenSqliteDatabasesInBackground(
                this.workspace.state.syncPolicy ?? (this.workspace.state.syncPaused ? 'paused' : 'on-demand'),
                true,
            )
            this.emitSdkObserverEvent('sqlite', 'debug', 'Reused cached SQLite database handle after mutex wait.', {
                blobId: this.blobId,
                path,
                cacheHit: true,
            })
            this.emitSdkObserverMetric('sdk.sqlite.open.duration_ms', Math.max(0, Date.now() - startedAt), {
                blobId: this.blobId,
                cacheHit: 'true',
            })
            return cachedAfterMutex
        }

        try {
            let db: SqliteDatabase
            let unregisterSync = () => {}
            let browserConfigCache: BrowserSqliteOpenConfig | null = null
            const getBrowserConfig = () => {
                browserConfigCache ??= this.requireBrowserSqliteOpenConfig(path)
                return browserConfigCache
            }
            const configuredBackend = options?.backend
                ?? (hasBrowserSqliteOpenConfigProvider(this.workspace)
                    ? getBrowserConfig().useSqlite3Mt?.backend
                    : undefined)
                ?? 'sqlite3mt'

            if (configuredBackend === 'sqlite3mtlight') {
                const browserConfig = {
                    ...getBrowserConfig(),
                    backgroundSync,
                } satisfies BrowserSqliteOpenConfig
                sdkConsole.debug('[Session] openSqlite: sqlite3mtlight websocket path', {
                    path,
                    blobId: this.blobId,
                    canWrite: browserConfig.canWrite,
                })
                db = await openSqlite3MtLightDatabase(browserConfig)
            } else if (shouldUseBrowserSqliteWorkerOnUiThread()) {
                const browserConfig = {
                    ...getBrowserConfig(),
                    backgroundSync,
                } satisfies BrowserSqliteOpenConfig
                sdkConsole.debug('[Session] openSqlite: browser worker path', {
                    path,
                    blobId: this.blobId,
                    headBlobId: buildBrowserSqliteHeadBlobId(this.blobId, path),
                    canWrite: browserConfig.canWrite,
                })
                db = await openBrowserSqliteDatabase(browserConfig)
                unregisterSync = this.registerBrowserSqliteHeadSync(path, db)
            } else {
                const engineFactory = await resolveWorkspaceSqliteEngineFactory(
                    this.workspace as WorkspaceSqliteEngineFactoryProvider
                )

                // §96 sqlite3mt is the production SQLite engine. sqlite3mt's
                // control worker owns the entire sync lifecycle (push, reconcile,
                // checkpoint). Reads/writes are intercepted via SAB VFS and the
                // control worker drives remote head changes through its own
                // reconcile/import path — no page-level sync wiring needed.
                db = await openDatabaseWithEngineFactory({
                    workspace: `${this.blobId}:${path}`,
                    backgroundSync,
                }, engineFactory)
            }

            if (options?.syncIntervalMs != null) {
                this._sqliteSyncIntervals.set(db, options.syncIntervalMs)
            }
            this._sqliteBackgroundSyncModes.set(db, backgroundSync)
            const tracked = this.trackOpenSqliteDatabase(path, db, unregisterSync)
            this.applyWorkspaceSyncPolicyToOpenSqliteDatabasesInBackground(
                this.workspace.state.syncPolicy ?? (this.workspace.state.syncPaused ? 'paused' : 'on-demand'),
                true,
            )
            this.emitSdkObserverEvent('sqlite', 'info', 'Opened SQLite database.', {
                blobId: this.blobId,
                path,
                cacheHit: false,
            })
            this.emitSdkObserverMetric('sdk.sqlite.open.duration_ms', Math.max(0, Date.now() - startedAt), {
                blobId: this.blobId,
                cacheHit: 'false',
            })
            return tracked
        } catch (error) {
            this.emitSdkObserverEvent('sqlite', 'error', 'Failed to open SQLite database.', {
                blobId: this.blobId,
                path,
                error: error instanceof Error ? error.message : String(error),
            })
            // §290: Capture operational error for diagnostic snapshot
            if (error instanceof SdkError) {
                this.captureError(error.code, error.message)
            } else if (error instanceof Error) {
                this.captureError('UNKNOWN', error.message)
            } else {
                this.captureError('UNKNOWN', String(error))
            }
            throw error
        } finally {
            releaseLock()
        }
    }

    // ── Diagnostics (§290) ─────────────────────────────────────────

    getDiagnosticSnapshot(): WorkspaceDiagnosticSnapshot {
        this.ensureOpen()

        const syncState = this.workspace.state

        // Collect open SQLite database diagnostics from tracked dbs
        const sqliteDatabases = this.openDatabases.map(db => {
            const diag = db.diagnostics?.() ?? null
            return {
                path: (db as unknown as { path?: string }).path ?? '(unknown)',
                isOpen: db.isOpen,
                syncState: diag?.sync?.state ?? 'unknown',
                remoteEpoch: diag?.sync?.remoteEpoch ?? 0,
                dirtyPageCount: diag?.memory?.dirtyPageCount ?? 0,
            }
        })

        return Object.freeze({
            capturedAt: new Date().toISOString(),
            session: Object.freeze({
                blobId: this.blobId,
                label: this.label,
                emoji: this.emoji,
                canWrite: this.canWrite,
                sessionAgeMs: this.closed ? 0 : Date.now() - this.sessionOpenedAt,
                isOpen: !this.closed,
            }),
            sync: Object.freeze({
                syncing: syncState.syncing,
                synced: syncState.synced,
                online: syncState.online,
                connectivity: syncState.connectivity,
                pendingChanges: syncState.pendingChanges,
                localHead: syncState.localHead ?? null,
                remoteHead: syncState.remoteHead ?? null,
                lastSyncError: syncState.lastSyncError
                    ? { code: syncState.lastSyncError.code, message: syncState.lastSyncError.message }
                    : null,
                lastSyncAt: syncState.lastSyncAt ?? null,
            }),
            sqliteDatabases: Object.freeze(sqliteDatabases),
            recentEvents: Object.freeze([...this._recentEvents].reverse()),
            recentErrors: Object.freeze([...this._recentErrors].reverse()),
        })
    }

    // ── Lifecycle ────────────────────────────────────────────────

    close(): void {
        if (this.closed) return // Idempotent (§80 §9.3)
        this.closed = true
        this.unsubExternalChange() // P4: Prevent stale callbacks + memory leak
        this.unsubStateChange()
        this.listCache.clear()

        // §95: Clean up SQLite sync subscriptions
        for (const unsub of this._sqliteUnsubFns) {
            try { unsub() } catch { /* cleanup best-effort */ }
        }
        this._sqliteUnsubFns = []

        // §95: Close all open SQLite databases (connections, queries, WASM)
        for (const db of this.openDatabases) {
            try { db.close() } catch { /* cleanup best-effort */ }
        }
        this.openDatabases = []

        try {
            this.workspace.close()
        } catch (err) {
            sdkConsole.warn('[WorkspaceSession] close() cleanup error:', err)
        }
    }

    // ── Guards ───────────────────────────────────────────────────

    private ensureOpen(): void {
        if (this.closed) {
            throw new SdkError('WORKSPACE_CLOSED', 'Operation on closed workspace session')
        }
    }

    private ensureWrite(): void {
        if (!this.workspace.canWrite) {
            throw new SdkError('WRITE_FORBIDDEN', 'Write operation on read-only workspace')
        }
    }

    /** §290: Record an operational error into the diagnostic ring buffer (capped at 20). */
    private captureError(code: string, message: string): void {
        this._recentErrors.push({ code, message, timestamp: Date.now() })
        if (this._recentErrors.length > 20) this._recentErrors.shift()
    }

    private emitSdkObserverEvent(
        domain: string,
        level: 'debug' | 'info' | 'warn' | 'error',
        message: string,
        data?: unknown,
    ): void {
        // §290: Capture warn/error events into diagnostic ring buffer
        if (level === 'warn' || level === 'error') {
            this._recentEvents.push({ domain, level, message, timestamp: Date.now() })
            if (this._recentEvents.length > 50) this._recentEvents.shift()
        }
        ;(this.workspace as WorkspaceObserverBridge).emitSdkObserverEvent?.(domain, level, message, data)
    }

    private emitSdkObserverMetric(
        name: string,
        value: number,
        tags?: Record<string, string>,
    ): void {
        (this.workspace as WorkspaceObserverBridge).emitSdkObserverMetric?.(name, value, tags)
    }
}

/**
 * Convenience helper: subscribe to all workspace event types at once.
 * Returns a single unsubscribe function that tears down all subscriptions.
 * Reduces boilerplate for external consumers who need all event channels.
 *
 * @param session  A `WorkspaceSession` instance (from `createSdk().openWorkspace()`)
 * @param handlers  Partial map of event handlers; missing events are simply not subscribed
 * @returns          A single teardown function — call it to remove all listeners
 *
 * @example
 * const unsub = subscribeWorkspaceEvents(session, {
 *   'state:changed': (payload) => console.log('sync state:', payload),
 *   'fs:changed':    ({ paths })      => console.log('changed:', paths),
 *   'history:changed': ({ newCommits }) => console.log('new commits:', newCommits),
 * })
 * // later: unsub()
 */
export function subscribeWorkspaceEvents(
    session: WorkspaceSession,
    handlers: Partial<{
        [E in WorkspaceEventType]: (payload: WorkspaceEventPayload[E]) => void
    }>,
): () => void {
    const unsubs: Array<() => void> = []
    for (const [event, handler] of Object.entries(handlers) as [
        WorkspaceEventType,
        (payload: WorkspaceEventPayload[WorkspaceEventType]) => void,
    ][]) {
        if (handler) {
            unsubs.push(session.on(event, handler))
        }
    }
    return () => {
        for (const unsub of unsubs) {
            unsub()
        }
    }
}
